import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import { getProposals, fetchProposalPdf, resendProposal, fetchProposalDocument, ensureShareToken, sharedPdfUrl, saveProposalPdf } from "../lib/api";
import { buildBlob, blobToBase64, fmtDate } from "../lib/proposalPdf";
import { useAuth } from "../context/AuthContext";
import { ELECTION_TYPES, GOVERNING_REGULATIONS } from "../lib/costEngine";
import { SendEmailDialog } from "../components/admin/SendEmailDialog";
import { TeamAccessCard } from "../components/admin/TeamAccessCard";
import { RateSettingsCard } from "../components/admin/RateSettingsCard";
import { ProposalListPanel } from "../components/admin/ProposalListPanel";
import { ProposalDetailPanel } from "../components/admin/ProposalDetailPanel";
import { Button } from "../components/ui/button";
import { RefreshCw, FileSpreadsheet, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(""); // "download" | "view" | "resend" | "share" | "doc"
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [emailOpen, setEmailOpen] = useState(false);
  const isOwner = user?.role === "owner";

  const load = async () => {
    setLoading(true);
    try {
      const data = await getProposals();
      setProposals(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch (e) {
      toast.error("Could not load proposals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => proposals.find((p) => p.id === selectedId) || null,
    [proposals, selectedId]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return proposals.filter((p) => {
      if (typeFilter !== "all" && p.electionType !== typeFilter) return false;
      if (!q) return true;
      const hay = [p.lead?.name, p.lead?.email, p.lead?.organization, p.eventTitle, p.location]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [proposals, query, typeFilter]);

  const exportCsv = () => {
    if (proposals.length === 0) {
      toast.error("No proposals to export.");
      return;
    }
    const rows = filtered;
    const headers = [
      "Client Name", "Email", "Job Title", "First Nation", "Phone",
      "Street/PO Box", "City", "Province", "Postal Code", "Event Title",
      "Election Type", "Governing Regulations", "Membership", "Location",
      "Total (CAD)", "Prepared By", "Captured",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((p) =>
      [
        p.lead?.name, p.lead?.email, p.lead?.jobTitle, p.lead?.firstNation || p.lead?.organization,
        p.lead?.phone, p.lead?.street, p.lead?.city, p.lead?.province, p.lead?.postalCode,
        p.eventTitle, ELECTION_TYPES[p.electionType]?.label || p.electionType,
        GOVERNING_REGULATIONS[p.governingRegulation] || "", p.membershipSize, p.location,
        p.total, p.preparedBy || "", fmtDate(p.createdAt),
      ].map(esc).join(",")
    );
    const csv = [headers.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `smartballot-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} lead${rows.length === 1 ? "" : "s"} to CSV.`);
  };

  const openPdf = async (mode) => {
    if (!selected) return;
    setBusy(mode);
    try {
      let blob;
      try {
        blob = await fetchProposalPdf(selected.id); // recorded copy
      } catch (e) {
        blob = await buildBlob(selected); // fallback: regenerate
      }
      const url = URL.createObjectURL(blob);
      if (mode === "view") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(selected.eventTitle || "SmartBallot-Proposal").replace(/\s+/g, "-")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      toast.error("Could not open the proposal.");
    } finally {
      setBusy("");
    }
  };

  const handleResend = async () => {
    if (!selected) return;
    setBusy("resend");
    try {
      const res = await resendProposal(selected.id);
      toast.success(`Proposal re-sent to ${res.to}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend this proposal.");
    } finally {
      setBusy("");
    }
  };

  const handleDownloadDoc = async () => {
    if (!selected) return;
    setBusy("doc");
    try {
      const blob = await fetchProposalDocument(selected.id);
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error("Could not open the governing document.");
    } finally {
      setBusy("");
    }
  };

  const handleRegenerate = async () => {
    if (!selected) return;
    setBusy("regen");
    try {
      const blob = await buildBlob(selected);
      // Refresh the stored copy so resend/share/download use the latest format.
      const b64 = await blobToBase64(blob);
      await saveProposalPdf(selected.id, b64);
      // Give the admin an immediate copy too.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(selected.eventTitle || "SmartBallot-Proposal").replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF regenerated with the latest format and saved.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not regenerate the PDF.");
    } finally {
      setBusy("");
    }
  };

  const handleCopyShareLink = async () => {
    if (!selected) return;
    setBusy("share");
    try {
      const { shareToken } = await ensureShareToken(selected.id);
      const url = sharedPdfUrl(shareToken);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Shareable link copied to clipboard.");
      } catch (err) {
        window.prompt("Copy this shareable link:", url);
      }
    } catch (e) {
      toast.error("Could not create a shareable link.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header active="admin" />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-800 text-4xl text-foreground tracking-tight">
              Leads & Proposals
            </h1>
            <p className="text-muted-foreground mt-2">
              Every captured client, their proposal, and one-click resend or client delivery.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-sm text-muted-foreground mr-1" data-testid="signed-in-email">
              {user?.email}
            </span>
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn" className="gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Export CSV
            </Button>
            <Button variant="outline" onClick={load} data-testid="refresh-btn" className="gap-2">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="gap-2">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>

        {isOwner && <TeamAccessCard />}

        <RateSettingsCard />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <ProposalListPanel
            loading={loading}
            proposals={proposals}
            filtered={filtered}
            query={query}
            setQuery={setQuery}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            selectedId={selectedId}
            onSelect={(p) => setSelectedId(p.id)}
          />
          <section className="lg:col-span-7">
            <ProposalDetailPanel
              selected={selected}
              busy={busy}
              onView={() => openPdf("view")}
              onDownload={() => openPdf("download")}
              onResend={handleResend}
              onRegenerate={handleRegenerate}
              onCopyShareLink={handleCopyShareLink}
              onDownloadDoc={handleDownloadDoc}
              onOpenEmail={() => setEmailOpen(true)}
            />
          </section>
        </div>
      </main>
      <SendEmailDialog
        proposals={selected ? [selected] : []}
        open={emailOpen}
        onOpenChange={setEmailOpen}
        onSent={load}
      />
    </div>
  );
}
